import { IntlShape } from 'react-intl';
import { ValueOf } from 'fym-common/src/types/utilityTypes';
import { InfoWidgetType, PlayerWidgetType, RawWidgetData, SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { DEFAULT_MEDIA_WIDGET_OPTIONS } from 'fym-shared/src/smartlinks/mediaWidget';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { DEFAULT_PRESAVE_SUBSCRIBE_SERVICES } from '../../widgets/views/presaveSubscribeWidget/presaveSubscribeServices';
import { pickerMessages } from './pickerMessages';

export interface DefaultBlockContext {
  /** The song of the page, else the newest song: Player and Media start from it. */
  song: CatalogItem | null;
  /** Every song: a Player takes the first Spotify link it finds. */
  songs: readonly CatalogItem[];
  /** The account address: a new Link block starts on it, and the form opens to change it. */
  accountUrl: string | null;
}

export type DefaultBlockData = { data: ValueOf<RawWidgetData> } | { error: string };

function spotifyLinkOf(song: CatalogItem | null): string | undefined {
  return song?.serviceLinks?.[MusicAPIIntegrationID.Spotify];
}

/**
 * The data a new block starts with when a picker card adds it at once (design b1). The form opens
 * right after, so the artist changes it in place. A block that needs a song says so when there is
 * none.
 */
export function defaultBlockData(
  intl: IntlShape,
  type: SmartLinkWidgetType,
  context: DefaultBlockContext,
  catalogItemUuid?: string
): DefaultBlockData {
  switch (type) {
    case SmartLinkWidgetType.info:
      return { data: { text: intl.formatMessage(pickerMessages.defaultText), type: InfoWidgetType.normal } };
    case SmartLinkWidgetType.link:
      return context.accountUrl === null
        ? { error: intl.formatMessage(pickerMessages.needsAddress) }
        : { data: { url: context.accountUrl, label: null } };
    case SmartLinkWidgetType.player: {
      const url = spotifyLinkOf(context.song) ?? context.songs.map(spotifyLinkOf).find((one) => one !== undefined);
      return url === undefined
        ? { error: intl.formatMessage(pickerMessages.needsSpotify) }
        : { data: { url, type: PlayerWidgetType.normal, version: 2 } };
    }
    case SmartLinkWidgetType.media:
      return context.song === null
        ? { error: intl.formatMessage(pickerMessages.needsSong) }
        : { data: { ...DEFAULT_MEDIA_WIDGET_OPTIONS, source: { kind: 'cover', catalogItemUuid: context.song.uuid } } };
    case SmartLinkWidgetType.socialLinks:
      return { data: {} };
    case SmartLinkWidgetType.profile:
      return { data: {} };
    case SmartLinkWidgetType.presaveSubscribe:
      return { data: { services: [...DEFAULT_PRESAVE_SUBSCRIBE_SERVICES] } };
    case SmartLinkWidgetType.presave:
      return catalogItemUuid === undefined
        ? { error: intl.formatMessage(pickerMessages.needsSong) }
        : { data: { catalogItemUuid, services: [...defaultPresaveServices] } };
    case SmartLinkWidgetType.image:
    default:
      // The Image block is gone from the picker: Media replaces it (owner 2026-10-08).
      return { error: intl.formatMessage(pickerMessages.addFailed) };
  }
}
